
// Read/edit rendering of the entry grid. Plain <td> text (no per-cell components); the only
// live editor is a single SpreadsheetCellEditor mounted into the focused cell while editing.
//
// Selection is a single rectangle described by an anchor (where the selection started) and a
// focus (the active cell = where editing happens). Click sets anchor+focus; shift-click and
// shift+arrows move the focus while keeping the anchor, growing/shrinking the rectangle.
// Selection + navigation live here (view concern); the dirty overlay and persistence live in
// the parent (data concern) - edits are emitted up via `cell-commit`.
import SpreadsheetCellEditor from './SpreadsheetCellEditor.vue'
import { cellApplies, originalCellString, dirtyKey } from './cell'
import { toTSV, fromTSV } from './tsv'

const MULTILINE_TYPES = [ 'Text', 'Object', 'RichText' ]

export default {
	name: 'SpreadsheetGrid',
	components: { SpreadsheetCellEditor },
	props: {
		columns: { type: Array, required: true },
		entries: { type: Array, required: true },
		typeLookup: { type: Object, required: true },
		// sparse overlay of edited cells: dirtyKey(entryId, columnKey) -> new string value
		dirty: { type: Object, required: true },
		// per-row save outcome: rowId -> { state, message }
		rowStatus: { type: Object, default: () => ({}) },
	},
	data: () => ({
		anchorRow: -1,
		anchorCol: -1,
		selRow: -1, // focus cell (active)
		selCol: -1,
		editing: false,
		dragging: false,
		// absolute position/size of the editor overlay, measured from the focused cell
		editorStyle: {} as Record<string, string>,
		// floating (synced) header shown when the real header scrolls under the app bar
		showFloatingHeader: false,
		floatingStyle: {} as Record<string, string>,
		headerCellWidths: [] as number[],
		scrollLeft: 0,
		floatingRaf: 0,
		floatingHandler: null as null | (() => void),
	}),
	computed: {
		// content types available for a new row's Type selector, by display name
		contentTypeOptions(): { id: string, name: string }[] {
			return Object.values(this.typeLookup ?? {})
				.map((t: any) => ({ id: t.sys.id, name: t.name }))
				.sort((a, b) => a.name.localeCompare(b.name))
		},
		// the focused cell's entry/column and the string/flags the overlay editor binds to
		selEntry(): any { return this.selRow >= 0 ? (this.entries[this.selRow] as any) : null },
		selColumn(): any { return this.selCol >= 0 ? (this.columns[this.selCol] as any) : null },
		editorValue(): string { return this.selEntry && this.selColumn ? this.displayString(this.selEntry, this.selColumn) : '' },
		editorMultiline(): boolean { return this.selColumn ? this.isMultiline(this.selColumn) : false },
		// normalized rectangle covering anchor..focus, or null when nothing is selected
		range(): { r0: number, r1: number, c0: number, c1: number } | null {
			if (this.selRow < 0 || this.anchorRow < 0) return null
			return {
				r0: Math.min(this.anchorRow, this.selRow),
				r1: Math.max(this.anchorRow, this.selRow),
				c0: Math.min(this.anchorCol, this.selCol),
				c1: Math.max(this.anchorCol, this.selCol),
			}
		},
	},
	methods: {
		editable(entry: any, column: any): boolean {
			return !column.readOnly && cellApplies(this.typeLookup, entry, column)
		},
		isNewRow(entry: any): boolean {
			return !!entry.sys?._new
		},
		statusState(entry: any): string {
			return this.rowStatus[entry.sys.id]?.state ?? ''
		},
		rowTypeId(entry: any): string {
			return entry.sys?.contentType?.sys?.id || ''
		},
		onTypeSelect(entry: any, e: Event) {
			this.$emit('row-type-change', { entryId: entry.sys.id, contentTypeId: (e.target as HTMLSelectElement).value })
		},
		isMultiline(column: any): boolean {
			return MULTILINE_TYPES.includes(column.field?.type)
		},
		displayString(entry: any, column: any): string {
			const override = this.dirty[dirtyKey(entry.sys.id, column.key)]
			if (override !== undefined) return override
			return originalCellString(this.typeLookup, entry, column)
		},
		cellClass(entry: any, column: any, r: number, c: number) {
			const b = this.range
			return {
				sys: column.kind === 'sys',
				na: !cellApplies(this.typeLookup, entry, column),
				dirty: this.dirty[dirtyKey(entry.sys.id, column.key)] !== undefined,
				range: !!b && r >= b.r0 && r <= b.r1 && c >= b.c0 && c <= b.c1,
				focus: this.selRow === r && this.selCol === c,
			}
		},
		focusGrid() {
			(this.$refs.grid as HTMLElement)?.focus({ preventScroll: true })
		},
		setFocus(r: number, c: number, extend: boolean) {
			this.selRow = r
			this.selCol = c
			if (!extend || this.anchorRow < 0) { this.anchorRow = r; this.anchorCol = c }
		},
		onCellMouseDown(e: MouseEvent, r: number, c: number) {
			if (this.editing) return // active editor commits via its own blur
			if (e.button !== 0) return // left button only
			// let interactive controls (e.g. the new-row type selector) work normally
			if ((e.target as HTMLElement).closest('select, input, textarea, option')) return
			// we manage selection ourselves; suppress the browser's native text selection /
			// drag so click-drag and shift-click extend the block cleanly
			e.preventDefault()
			this.setFocus(r, c, e.shiftKey)
			// begin a drag-select: entering other cells extends the block until mouseup
			this.dragging = true
			window.addEventListener('mouseup', this.endDrag)
			this.focusGrid()
		},
		onCellMouseEnter(r: number, c: number) {
			if (!this.dragging || this.editing) return
			this.setFocus(r, c, true) // extend, keeping the anchor
		},
		endDrag() {
			this.dragging = false
			window.removeEventListener('mouseup', this.endDrag)
		},
		editCell(r: number, c: number) {
			this.setFocus(r, c, false)
			if (this.editable(this.entries[r], this.columns[c])) {
				this.updateEditorStyle()
				this.editing = true
			}
		},
		// measure the focused cell (relative to the scrolling wrapper) so the editor overlay
		// sits exactly over it without changing table layout
		updateEditorStyle() {
			const inner = this.$refs.inner as HTMLElement
			const td = inner?.querySelector(`td[data-r="${this.selRow}"][data-c="${this.selCol}"]`) as HTMLElement | null
			if (!inner || !td) return
			const cell = td.getBoundingClientRect()
			const base = inner.getBoundingClientRect()
			const minHeight = this.editorMultiline ? Math.max(cell.height, 80) : cell.height
			this.editorStyle = {
				top: (cell.top - base.top) + 'px',
				left: (cell.left - base.left) + 'px',
				width: cell.width + 'px',
				minHeight: minHeight + 'px',
			}
		},
		moveSel(dr: number, dc: number, extend: boolean) {
			if (this.selRow < 0) this.setFocus(0, 0, false)
			else {
				const r = Math.min(Math.max(this.selRow + dr, 0), this.entries.length - 1)
				const c = Math.min(Math.max(this.selCol + dc, 0), this.columns.length - 1)
				this.setFocus(r, c, extend)
			}
			// keyboard navigation should keep the focused cell visible (click/drag do not scroll)
			this.scrollFocusIntoView()
		},
		// scroll the focused cell just into view in both the horizontal grid scroller and the
		// vertical page scroll; 'nearest' means no movement when it is already visible
		scrollFocusIntoView() {
			this.$nextTick(() => {
				const inner = this.$refs.inner as HTMLElement
				const td = inner?.querySelector(`td[data-r="${this.selRow}"][data-c="${this.selCol}"]`) as HTMLElement | null
				td?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
			})
		},
		onKeydown(e: KeyboardEvent) {
			if (this.editing) return
			switch (e.key) {
				case 'ArrowUp': this.moveSel(-1, 0, e.shiftKey); e.preventDefault(); break
				case 'ArrowDown': this.moveSel(1, 0, e.shiftKey); e.preventDefault(); break
				case 'ArrowLeft': this.moveSel(0, -1, e.shiftKey); e.preventDefault(); break
				case 'ArrowRight': this.moveSel(0, 1, e.shiftKey); e.preventDefault(); break
				case 'Enter':
				case 'F2':
					if (this.selRow >= 0) { this.editCell(this.selRow, this.selCol); e.preventDefault() }
					break
				case 'Escape': this.clearSelection(); break
			}
		},
		clearSelection() {
			this.anchorRow = this.anchorCol = this.selRow = this.selCol = -1
		},
		onCommit(payload: { value: string, move: boolean }) {
			const entry = this.entries[this.selRow] as any
			const column = this.columns[this.selCol] as any
			this.$emit('cell-commit', {
				entryId: entry.sys.id,
				columnKey: column.key,
				value: payload.value,
				original: originalCellString(this.typeLookup, entry, column),
			})
			this.editing = false
			this.focusGrid()
			if (payload.move) this.moveSel(1, 0, false)
		},
		onCancel() {
			this.editing = false
			this.focusGrid()
		},
		// CMD/CTRL-C: copy the selected block to the clipboard as TSV. While editing we let the
		// input's native copy happen (guard returns early).
		onCopy(e: ClipboardEvent) {
			if (this.editing || !this.range) return
			const b = this.range
			const rows: string[][] = []
			for (let r = b.r0; r <= b.r1; r++) {
				const cells: string[] = []
				for (let c = b.c0; c <= b.c1; c++)
					cells.push(this.displayString(this.entries[r] as any, this.columns[c] as any))
				rows.push(cells)
			}
			e.clipboardData?.setData('text/plain', toTSV(rows))
			e.preventDefault()
		},
		// resolve a pasted Type cell (content type id, or a display name) to a content type id
		resolveContentTypeId(raw: string): string | null {
			const v = (raw ?? '').trim()
			if (!v) return null
			if (this.typeLookup?.[v]) return v // already an id
			const byName = Object.values(this.typeLookup ?? {})
				.find((t: any) => t.name?.toLowerCase() === v.toLowerCase()) as any
			return byName?.sys?.id ?? null
		},
		// CMD/CTRL-V: parse pasted TSV and write it into the sheet starting at the selection's
		// top-left corner. Cells that are read-only or not applicable to a row are skipped, and
		// anything past the grid bounds is ignored. Each write is emitted as a cell edit so the
		// parent's dirty overlay stays the single source of truth.
		//
		// New rows need special handling: their Type cell is a selector, not a normal cell, and
		// the field columns only become writable once the type is set. So we set the content type
		// for any pasted new rows first (synchronously, via $emit), then write the rest of the
		// cells on the next tick once the columns have recomputed for the chosen types.
		onPaste(e: ClipboardEvent) {
			if (this.editing || !this.range) return
			const text = e.clipboardData?.getData('text/plain') ?? ''
			if (!text) return
			e.preventDefault()
			const matrix = fromTSV(text)
			const startR = this.range.r0
			const startC = this.range.c0

			// pass 1: set the content type of any new rows the paste covers
			const typeCol = this.columns.findIndex((c: any) => c.key === 'sys.contentType')
			if (typeCol >= 0) {
				for (let i = 0; i < matrix.length; i++) {
					const r = startR + i
					const entry = this.entries[r] as any
					if (!entry || !this.isNewRow(entry)) continue
					const j = typeCol - startC
					if (j < 0 || j >= matrix[i].length) continue
					const typeId = this.resolveContentTypeId(matrix[i][j])
					if (typeId) this.$emit('row-type-change', { entryId: entry.sys.id, contentTypeId: typeId })
				}
			}

			// pass 2: write the remaining cells after columns reflect the new rows' types
			this.$nextTick(() => this.applyPaste(matrix, startR, startC))
		},
		applyPaste(matrix: string[][], startR: number, startC: number) {
			let maxCols = 0
			for (let i = 0; i < matrix.length; i++) {
				maxCols = Math.max(maxCols, matrix[i].length)
				for (let j = 0; j < matrix[i].length; j++) {
					const r = startR + i
					const c = startC + j
					if (r >= this.entries.length || c >= this.columns.length) continue
					const entry = this.entries[r] as any
					const column = this.columns[c] as any
					if (column.key === 'sys.contentType') continue // handled in pass 1
					if (!this.editable(entry, column)) continue
					this.$emit('cell-commit', {
						entryId: entry.sys.id,
						columnKey: column.key,
						value: matrix[i][j],
						original: originalCellString(this.typeLookup, entry, column),
					})
				}
			}
			// select the pasted region
			this.anchorRow = startR
			this.anchorCol = startC
			this.selRow = Math.min(startR + matrix.length - 1, this.entries.length - 1)
			this.selCol = Math.min(startC + maxCols - 1, this.columns.length - 1)
		},
		// --- floating header (keeps the header row visible during page scroll) ---
		// copy the real header cells' rendered widths so the floating row lines up with the body
		measureHeader() {
			const inner = this.$refs.inner as HTMLElement
			if (!inner) return
			const ths = inner.querySelectorAll('thead th')
			this.headerCellWidths = Array.from(ths).map(th => (th as HTMLElement).getBoundingClientRect().width)
		},
		// show/position the floating header relative to the sticky app bar and current scroll
		updateFloating() {
			const grid = this.$refs.grid as HTMLElement
			if (!grid) return
			// bottom edge of the app's sticky header = where our floating row should sit
			const head = document.querySelector('.head') as HTMLElement | null
			const offset = head ? head.getBoundingClientRect().bottom : 110
			const rect = grid.getBoundingClientRect()
			// visible once the real header is tucked under the app bar, while rows remain below it
			this.showFloatingHeader = rect.top < offset && rect.bottom > offset + 24
			if (!this.showFloatingHeader) return
			if (!this.headerCellWidths.length) this.measureHeader()
			this.scrollLeft = grid.scrollLeft
			this.floatingStyle = {
				top: offset + 'px',
				left: rect.left + 'px',
				width: grid.clientWidth + 'px',
			}
		},
		scheduleFloating() {
			if (this.floatingRaf) return
			this.floatingRaf = requestAnimationFrame(() => {
				this.floatingRaf = 0
				this.updateFloating()
			})
		},
	},
	watch: {
		// header cell widths depend on the columns/content; re-measure on the next update
		columns() { this.headerCellWidths = []; this.scheduleFloating() },
		entries() { this.headerCellWidths = []; this.scheduleFloating() },
	},
	mounted() {
		// capture=true so the single listener catches both the page's vertical scroll and the
		// grid's own horizontal scroll (scroll doesn't bubble, but is seen in the capture phase)
		this.floatingHandler = () => this.scheduleFloating()
		window.addEventListener('scroll', this.floatingHandler, true)
		window.addEventListener('resize', this.floatingHandler)
		this.$nextTick(() => this.updateFloating())
	},
	beforeDestroy() {
		window.removeEventListener('mouseup', this.endDrag)
		if (this.floatingHandler) {
			window.removeEventListener('scroll', this.floatingHandler, true)
			window.removeEventListener('resize', this.floatingHandler)
		}
		if (this.floatingRaf) cancelAnimationFrame(this.floatingRaf)
	},
}
